const giftEntryLayouts = new WeakSet();

/** Move the existing PDP entry only; builder cards and mobile keep their layout. */
export function mountGiftEntryLayout(root) {
  if (giftEntryLayouts.has(root)) return;
  const faq = document.querySelector('.faq-wrapper');
  const faqSection = faq?.closest('.shopify-section');
  if (!faqSection || faqSection.contains(root)) return;
  giftEntryLayouts.add(root);
  const home = document.createComment('Instant Present mobile position');
  root.before(home);
  const slot = document.createElement('div');
  slot.className = 'gift-product-entry-slot section section--page-width';
  const desktop = matchMedia('(min-width: 750px)');
  const sync = () => {
    if (desktop.matches) {
      faqSection.after(slot);
      slot.append(root);
      root.classList.add('gift-product-entry--after-faq');
    } else {
      home.after(root);
      root.classList.remove('gift-product-entry--after-faq');
      slot.remove();
    }
  };
  desktop.addEventListener('change', sync);
  sync();
}

/** A native link owns navigation; no embedded creator or popup state. */
export function giftEntryUrl(href, search) {
  const url = new URL(href);
  const variant = new URLSearchParams(search).get('variant');
  if (/^\d{1,20}$/.test(variant || '')) url.searchParams.set('v', variant);
  return url;
}
export function mountGiftEntry(root, finish) {
  const open = root.querySelector('[data-gift-open]');
  const status = root.querySelector('[data-gift-entry-status]');
  const sync = () => { open.href = giftEntryUrl(open.href, location.search).href; };
  // Browser menus and keyboard navigation read href before any click occurs.
  for (const event of ['click', 'auxclick', 'contextmenu', 'focus']) open.addEventListener(event, sync);
  // Completion runs in this tab after the creator returns. No opener required.
  const token = new URLSearchParams(location.search).get('gift');
  if (/^[A-Za-z0-9_-]{22}$/.test(token || '')) {
    finish(token, text => { status.textContent = text; })
      .catch(() => { status.textContent = 'Could not open your cart. Please try again.'; });
  }
}
/** Fade between previews in place; never run JavaScript per frame. */
export function mountGiftPreview(preview) {
  if (!preview) return;
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  let cards = [], front = 0, timer, visible = false, stopped = false;
  const render = () => cards.forEach((card, index) => {
    card.dataset.position = index === front ? 'front' : 'hidden';
  });
  const sync = () => {
    clearInterval(timer);
    if (stopped || !visible || document.hidden || motion.matches || cards.length < 2) return;
    timer = setInterval(() => {
      if (!preview.isConnected) { stop(); return; }
      front = (front + 1) % cards.length;
      render();
    }, 2000);
  };
  const observer = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; sync(); });
  const stop = () => {
    stopped = true;
    clearInterval(timer);
    observer.disconnect();
    document.removeEventListener('visibilitychange', sync);
    motion.removeEventListener('change', sync);
    for (const event of ['pointerdown', 'touchstart', 'focusin']) preview.removeEventListener(event, stop);
  };
  for (const event of ['pointerdown', 'touchstart', 'focusin']) preview.addEventListener(event, stop, {passive:true});
  document.addEventListener('visibilitychange', sync);
  motion.addEventListener('change', sync);
  observer.observe(preview);
  // Skip failed previews rather than bringing a blank card to the front.
  const images = [...preview.querySelectorAll('img')];
  return Promise.all(images.map(image => image.decode().catch(() => {}))).then(() => {
    cards = images.filter(image => image.naturalWidth > 0);
    images.filter(image => !image.naturalWidth).forEach(image => { image.hidden = true; image.dataset.position = 'hidden'; });
    render();
    sync();
  });
}
if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-gift-preview]').forEach(mountGiftPreview);
  document.querySelectorAll('[data-gift-product-entry]').forEach(root => {
    mountGiftEntryLayout(root);
    mountGiftEntry(root, async (token, say) => {
      const {finishGift} = await import(root.dataset.finishModule);
      await finishGift('/apps/surprise', token, say);
    });
  });
}
